import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useEffect, useState } from 'react';
// @ts-ignore
import { useNavigate } from "react-router-dom";
import { gql, useApolloClient, useReactiveVar } from '@apollo/client';
import ContentEditable from "react-contenteditable";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy, } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDestroySubscriptionBenefitMutation, useSubscriptionBenefitComponentQuery, useWelcomeGiftComponentQuery, useGetBenefitQuery, DeliveryStrategy, GetAllRegularBenefitsDocument } from '../types';
import SvgX from '../icons/SvgX';
import SvgEdit24Px from '../icons/SvgEdit';
import SvgSettingsGear24Px from '../icons/SvgSettingsGear';
import { periodMap } from './SubscriptionBenefit';
import { deliveryValuesFor } from '../cache/SubscriptionBenefitFunctions';
import { ModalConfirmation } from "../../index";
import { stripHtml, preventEnter } from '../helpers/tools';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { BsBox, BsGift } from "react-icons/bs";
import { IconContext } from "react-icons";
import { currentWriteInBenefitVar, currentShopifyVersionVar, pageHasUnsavedChangesVar, currentBusinessIdVar, activeDropdownVar, focusedBenefitIdVar } from '../helpers/client';
import Button, { ButtonVariant } from './Button';
import { RiErrorWarningLine } from 'react-icons/ri';
import { MdAdd } from 'react-icons/md';
import { MdClose } from 'react-icons/md';
import { MdDragIndicator } from 'react-icons/md';
gql `
  query GetAllRegularBenefits($tierId: ID!) {
    membershipTier(id: $tierId) {
      id
      business {
        id
        membershipTiers(kind: Live) {
          id
          benefits {
            id
            __typename
            ... on RegularBenefit {
              order
            }
          }
        }
      }
    }
  }
`;
export function isSubscriptionBenefit(benefit) {
    // @ts-ignore
    return "__typename" in benefit && benefit.__typename === "SubscriptionBenefit";
}
export function isWelcomeGiftBenefit(benefit) {
    // @ts-ignore
    return "__typename" in benefit && benefit.__typename === "WelcomeGiftBenefit";
}
export function isRegularBenefit(benefit) {
    // @ts-ignore
    return "__typename" in benefit && benefit.__typename === "RegularBenefit";
}
const RegularBenefitComponent = ({ tier, benefit: { id }, dragHandleProps, isDragging }) => {
    var _a, _b, _c, _d, _e;
    const client = useApolloClient();
    const currentBusinessId = useReactiveVar(currentBusinessIdVar);
    const [destroyBenefit] = useDestroySubscriptionBenefitMutation();
    const activeDropdown = useReactiveVar(activeDropdownVar);
    const dropdownId = `${tier.id}-${id}`;
    const isOpen = activeDropdown === dropdownId;
    const focusedBenefitId = useReactiveVar(focusedBenefitIdVar);
    const isFocused = focusedBenefitId === id;
    useEffect(() => {
        function handleGlobalClick() {
            if (isOpen) {
                activeDropdownVar(null);
            }
        }
        if (isOpen) {
            setTimeout(() => {
                document.addEventListener('click', handleGlobalClick);
            }, 0);
        }
        return () => {
            document.removeEventListener('click', handleGlobalClick);
        };
    }, [isOpen]);
    const { data, loading, error } = useGetBenefitQuery({
        variables: {
            id,
        },
    });
    const editBenefitRef = useRef(null);
    const currentWriteInBenefit = currentWriteInBenefitVar();
    // Remove the benefitRef and use state instead
    const [benefitName, setBenefitName] = useState(((_a = data === null || data === void 0 ? void 0 : data.benefit) === null || _a === void 0 ? void 0 : _a.name) || "");
    // Update local state when cache data changes
    useEffect(() => {
        var _a;
        if ((_a = data === null || data === void 0 ? void 0 : data.benefit) === null || _a === void 0 ? void 0 : _a.name) {
            setBenefitName(data.benefit.name);
        }
    }, [(_b = data === null || data === void 0 ? void 0 : data.benefit) === null || _b === void 0 ? void 0 : _b.name]);
    const addBenefitToTiers = (id, mode, currentTierId) => {
        console.log("addBenefitToTiers", id, mode);
        client.cache.modify({
            id: `Business:${currentBusinessId}`,
            fields: {
                membershipTiers(existingTiers = [], { storeFieldName, readField }) {
                    var _a, _b, _c, _d, _e;
                    if (!storeFieldName.includes('"kind":"Live"'))
                        return existingTiers;
                    let currentTierPrice = null;
                    if (mode === 'higher-tiers') {
                        const monthlyPlan = (_a = tier.plans) === null || _a === void 0 ? void 0 : _a.find(p => p.kind === 'MONTHLY');
                        currentTierPrice = ((_b = monthlyPlan === null || monthlyPlan === void 0 ? void 0 : monthlyPlan.price) === null || _b === void 0 ? void 0 : _b.amount) ||
                            (((_e = (_d = (_c = tier.plans) === null || _c === void 0 ? void 0 : _c.find(p => p.kind === 'ANNUAL')) === null || _d === void 0 ? void 0 : _d.price) === null || _e === void 0 ? void 0 : _e.amount) || 0) / 12;
                    }
                    existingTiers.forEach((tierRef) => {
                        var _a, _b, _c, _d, _e;
                        // Skip tiers based on mode and conditions
                        if (mode === 'single' && readField('id', tierRef) !== currentTierId) {
                            return;
                        }
                        if (mode === 'higher-tiers') {
                            const monthlyPlan = (_a = readField('plans', tierRef)) === null || _a === void 0 ? void 0 : _a.find((p) => p.kind === 'MONTHLY');
                            const tierPrice = ((_b = monthlyPlan === null || monthlyPlan === void 0 ? void 0 : monthlyPlan.price) === null || _b === void 0 ? void 0 : _b.amount) || (((_e = (_d = (_c = readField('plans', tierRef)) === null || _c === void 0 ? void 0 : _c.find((p) => p.kind === 'ANNUAL')) === null || _d === void 0 ? void 0 : _d.price) === null || _e === void 0 ? void 0 : _e.amount) || 0) / 12;
                            if (!tierPrice || !currentTierPrice || tierPrice <= currentTierPrice) {
                                return;
                            }
                        }
                        client.cache.modify({
                            id: tierRef.__ref,
                            fields: {
                                benefits(existingBenefits = []) {
                                    const benefitRef = client.cache.identify({
                                        __typename: 'RegularBenefit',
                                        id
                                    });
                                    if (!existingBenefits.some((ref) => ref.__ref === benefitRef)) {
                                        return [...existingBenefits, { __ref: benefitRef }];
                                    }
                                    return existingBenefits;
                                }
                            }
                        });
                    });
                    return existingTiers;
                }
            }
        });
    };
    const updateBenefitName = (id) => {
        return (ev) => {
            const newName = stripHtml(ev.target.value);
            setBenefitName(newName);
            pageHasUnsavedChangesVar(true);
            client.writeFragment({
                id: `RegularBenefit:${id}`,
                fragment: gql `
          fragment SimpleBenefitFragment on Benefit {
            name
          }
        `,
                data: {
                    name: newName
                },
                broadcast: true
            });
        };
    };
    const removeBenefit = (id) => {
        return async () => {
            await destroyBenefit({ variables: { id } });
            client.cache.modify({
                id: `MembershipTier:${tier.id}`,
                fields: {
                    benefits(existingBenefitRefs = [], { readField }) {
                        if (!existingBenefitRefs) {
                            existingBenefitRefs = [];
                        }
                        return existingBenefitRefs.filter(ref => readField('id', ref) !== id);
                    }
                }
            });
        };
    };
    const handleFocus = () => {
        focusedBenefitIdVar(id);
    };
    const handleBlur = () => {
        focusedBenefitIdVar(null);
    };
    if (currentWriteInBenefit == id) {
        setTimeout(() => {
            editBenefitRef.current.focus();
            document.execCommand('selectAll', false, null);
            currentWriteInBenefitVar(null);
        }, 1);
    }
    const renderContent = (dragHandleProps) => {
        var _a, _b, _c;
        return (_jsxs("div", { className: "flex items-start justify-center w-full", children: [dragHandleProps && (_jsx("div", Object.assign({}, dragHandleProps, { className: "p-1 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600", children: _jsx(MdDragIndicator, { className: "w-5 h-5" }) }))), _jsx(ContentEditable, { contentEditable: true, html: benefitName, id: `benefit-name-${id}`, className: `py-1 px-2 text-center mr-2 underline focus:ring-transparent focus:outline-none focus:decoration-salmon-600 focus-within:decoration-salmon-600 selection:underline selection:bg-selectionBlue selection:decoration-salmon-600
          ${isFocused ? 'bg-salmon-200 border border-salmon-600 rounded' : ''}`, style: { width: '78%' }, innerRef: editBenefitRef, onChange: updateBenefitName(id), onKeyPress: preventEnter, onFocus: handleFocus, onBlur: handleBlur }), _jsxs("button", { className: "p-1 text-gray-600 hover:bg-gray-50 rounded-md", onClick: (e) => {
                        e.stopPropagation();
                        activeDropdownVar(isOpen ? null : dropdownId);
                    }, "aria-haspopup": "true", "aria-expanded": isOpen, "data-tool-html": "Add this benefit to other membership tiers", "data-tooltip-id": "benefit-list-add", children: [_jsx(MdAdd, { className: "w-5 h-5 fill-current" }), _jsx(ReactTooltip, { id: "benefit-list-add" })] }), _jsx("button", { className: "p-1 text-gray-600 hover:bg-gray-50 rounded-md", onClick: removeBenefit(id), children: _jsx(MdClose, { className: "w-5 h-5 text-wfGray-600 fill-current" }) }), isOpen && (_jsxs("div", { className: "absolute mt-2 py-2 w-48 bg-white rounded-md shadow-xl z-20", onClick: (e) => e.stopPropagation(), children: [_jsx("button", { className: "block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left", onClick: () => {
                                addBenefitToTiers(id, 'higher-tiers');
                                activeDropdownVar(null);
                            }, children: "Add to all higher tiers" }), _jsx("button", { className: "block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left", onClick: () => {
                                addBenefitToTiers(id, 'all');
                                activeDropdownVar(null);
                            }, children: "Add to all tiers" }), _jsxs(_Fragment, { children: [console.log('tier:', tier), console.log('tier.business:', tier.business), console.log('tier.business.membershipTiers:', (_a = tier.business) === null || _a === void 0 ? void 0 : _a.membershipTiers)] }), (_c = (_b = tier.business) === null || _b === void 0 ? void 0 : _b.membershipTiers) === null || _c === void 0 ? void 0 : _c.filter(t => t.id !== tier.id).map(t => {
                            console.log('Mapping tier:', t);
                            return (_jsxs("button", { className: "block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left", onClick: () => {
                                    addBenefitToTiers(id, 'single', t.id);
                                    activeDropdownVar(null);
                                }, children: ["Add to ", t.name] }, t.id));
                        })] }))] }));
    };
    return (_jsxs("div", { className: `relative w-full mb-4 leading-6 text-center ${isDragging ? 'opacity-50' : ''}`, children: [_jsx(IconContext.Provider, { value: { style: { display: 'inline-block' } }, children: _jsxs("div", { className: "flex items-start justify-center", children: [dragHandleProps && (_jsx("div", Object.assign({}, dragHandleProps, { className: "p-1 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600", children: _jsx(MdDragIndicator, { className: "w-5 h-5" }) }))), _jsx(ContentEditable, { contentEditable: true, html: benefitName, id: `benefit-name-${id}`, className: `py-1 px-2 text-center mr-2 underline focus:ring-transparent focus:outline-none focus:decoration-salmon-600 focus-within:decoration-salmon-600 selection:underline selection:bg-selectionBlue selection:decoration-salmon-600
              ${isFocused ? 'bg-salmon-200 border border-salmon-600 rounded' : ''}`, style: { width: '78%' }, innerRef: editBenefitRef, onChange: updateBenefitName(id), onKeyPress: preventEnter, onFocus: handleFocus, onBlur: handleBlur }), _jsxs("button", { className: "p-1 text-gray-600 hover:bg-gray-50 rounded-md", onClick: (e) => {
                                e.stopPropagation();
                                activeDropdownVar(isOpen ? null : dropdownId);
                            }, "aria-haspopup": "true", "aria-expanded": isOpen, "data-tool-html": "Add this benefit to other membership tiers", "data-tooltip-id": "benefit-list-add", children: [_jsx(MdAdd, { className: "w-5 h-5 fill-current" }), _jsx(ReactTooltip, { id: "benefit-list-add" })] }), _jsx("button", { className: "p-1 text-gray-600 hover:bg-gray-50 rounded-md", onClick: removeBenefit(id), children: _jsx(MdClose, { className: "w-5 h-5 text-wfGray-600 fill-current" }) }), isOpen && (_jsxs("div", { className: "absolute mt-2 py-2 w-48 bg-white rounded-md shadow-xl z-20", onClick: (e) => e.stopPropagation(), children: [_jsx("button", { className: "block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left", onClick: () => {
                                        addBenefitToTiers(id, 'higher-tiers');
                                        activeDropdownVar(null);
                                    }, children: "Add to all higher tiers" }), _jsx("button", { className: "block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left", onClick: () => {
                                        addBenefitToTiers(id, 'all');
                                        activeDropdownVar(null);
                                    }, children: "Add to all tiers" }), _jsxs(_Fragment, { children: [console.log('tier:', tier), console.log('tier.business:', tier.business), console.log('tier.business.membershipTiers:', (_c = tier.business) === null || _c === void 0 ? void 0 : _c.membershipTiers)] }), (_e = (_d = tier.business) === null || _d === void 0 ? void 0 : _d.membershipTiers) === null || _e === void 0 ? void 0 : _e.filter(t => t.id !== tier.id).map(t => {
                                    console.log('Mapping tier:', t);
                                    return (_jsxs("button", { className: "block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left", onClick: () => {
                                            addBenefitToTiers(id, 'single', t.id);
                                            activeDropdownVar(null);
                                        }, children: ["Add to ", t.name] }, t.id));
                                })] }))] }) }), _jsx("br", {}), _jsx("label", { className: "inline", htmlFor: `benefit-name-${id}`, children: _jsx(SvgEdit24Px, { onClick: () => {
                        editBenefitRef.current.focus();
                        setTimeout(() => {
                            document.execCommand('selectAll', false, null);
                        }, 0);
                    }, className: "text-wfGray-600 inline fill-current cursor-pointer" }) })] }, `benefit-id-${id}`));
};
gql `
#import "../gql/VariantFragment.graphql"
#import "../gql/AddonFragment.graphql"
query SubscriptionBenefitComponent($id: ID!) {
  benefit(id: $id) {
    __typename
    id
    name
    membershipTiers {
      id
      name
    }
    ...on SubscriptionBenefit {
      delivery
      nextShipmentDate
      deliveryFrequency
      shippingFrequency {
        __typename
        number
        period
      }
      curationPreferOverstock
      curationMatchMode
      products {
        id
        name
        platformId
        platformName
        platformVariantsOnlyMode
        curationCollections
        curationExcludeProducts
        variants {
          ...VariantFragment
        }
      }
      addons {
        ...AddonFragment
      }
      variants {
        ...VariantFragment
      }
      shopifyProductIds
      fulfillments {
        id
      }
    }
  }
}
query WelcomeGiftComponent($id: ID!) {
  benefit(id: $id) {
    __typename
    id
    name
    membershipTiers {
      id
      name
    }
    ...on WelcomeGiftBenefit {
      products {
        id
        name
        platformId
        platformName
        platformVariantsOnlyMode
        variants {
          ...VariantFragment
        }
      }
      variants {
        ...VariantFragment
      }
      shopifyProductIds
    }
  }
}
`;
const deliveryMapper = {
    [DeliveryStrategy.Annual]: "every year",
    [DeliveryStrategy.Quarterly]: "every three months",
    [DeliveryStrategy.Monthly]: "every month",
    [DeliveryStrategy.Biweekly]: "every two weeks",
    [DeliveryStrategy.Weekly]: "every week",
    [DeliveryStrategy.Custom]: "on a custom schedule",
};
const WelcomeGiftComponent = ({ tier, benefit: { id } }) => {
    const navigate = useNavigate();
    const client = useApolloClient();
    const [destroyBenefit] = useDestroySubscriptionBenefitMutation();
    const benefitRef = useRef(null);
    const updateBenefitNameRef = useRef(null);
    const { data, loading, error } = useWelcomeGiftComponentQuery({
        variables: {
            id,
        },
    });
    if (!(data === null || data === void 0 ? void 0 : data.benefit))
        return null;
    const benefit = data === null || data === void 0 ? void 0 : data.benefit;
    const updateBenefitNameContent = (id) => {
        return (ev) => {
            pageHasUnsavedChangesVar(true);
            benefitRef.current = stripHtml(ev.target.value) || "Welcome Gift";
            client.writeFragment({
                id: `WelcomeGiftBenefit:${id}`,
                fragment: gql `
          fragment UpdateWelcomeGiftBenefitName on WelcomeGiftBenefit {
            name
          }
        `,
                data: {
                    name: benefitRef.current
                },
                broadcast: false,
            });
        };
    };
    const removeBenefit = (id) => {
        return async () => {
            await destroyBenefit({
                variables: { id },
                update: (cache) => {
                    cache.modify({
                        id: `MembershipTier:${tier.id}`,
                        fields: {
                            welcomeGift: () => null,
                        }
                    });
                }
            });
        };
    };
    const handleClickEdit = () => {
        navigate(`/memberships/${benefit.membershipTiers[0].id}/welcome/${id}`);
    };
    benefitRef.current = benefitRef.current || data.benefit.name;
    return (_jsxs("li", { className: "relative w-full mb-4 leading-6 text-center", children: [_jsxs("div", { className: "relative", children: [_jsx(BsGift, { className: "absolute inline-block", style: { width: "1.3em", top: "0.25em", left: "0.3em", right: "0", verticalAlign: "top", marginRight: "0.3em", marginLeft: "-0.3em" }, onClick: handleClickEdit }), _jsx(ContentEditable, { contentEditable: true, html: benefitRef.current, id: `benefit-name-${benefit.id}`, className: "py-1 px-2 inline-block text-center mr-2 underline focus:ring-transparent focus:outline-none focus:decoration-salmon-600 focus-within:decoration-salmon-600 selection:underline selection:bg-selectionBlue selection:decoration-salmon-600", style: { width: '78%' }, innerRef: updateBenefitNameRef, onChange: updateBenefitNameContent(benefit.id), onKeyPress: preventEnter }), _jsx(SvgX, { onClick: removeBenefit(benefit.id), className: "text-wfGray-600 cursor-pointer fill-current absolute", style: { top: "0.225em", right: "-0.3em", transform: "scale(.7)" } })] }), _jsxs(Button, { className: "my-2", variant: ButtonVariant.Outlined, onClick: handleClickEdit, children: [_jsx(SvgSettingsGear24Px, { className: "text-wfGray-600 inline cursor-pointer align-middle fill-current mr-2" }), _jsx("span", { className: "align-middle", children: "Welcome Gift Settings" })] }), _jsx("div", { className: "w-full my-2" })] }, `benefit-id-${benefit.id}`));
};
const SubscriptionBenefitComponent = ({ tier, benefit: { id }, position }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const navigate = useNavigate();
    const client = useApolloClient();
    const [destroyBenefit] = useDestroySubscriptionBenefitMutation();
    const benefitRef = useRef(null);
    const updateBenefitNameRef = useRef(null);
    const currentShopifyVersion = useReactiveVar(currentShopifyVersionVar);
    const [showModal, setShowModal] = useState(false);
    const { data, loading, error } = useSubscriptionBenefitComponentQuery({
        variables: {
            id,
        },
    });
    if (!(data === null || data === void 0 ? void 0 : data.benefit))
        return null;
    const benefit = data === null || data === void 0 ? void 0 : data.benefit;
    // @ts-ignore
    if (!isSubscriptionBenefit(benefit))
        return null;
    const updateBenefitDelivery = (id) => {
        return (ev) => {
            pageHasUnsavedChangesVar(true);
            let deliveryValues = deliveryValuesFor(ev.target.value);
            if (deliveryValues.shippingFrequency) {
                // @ts-ignore
                deliveryValues.shippingFrequency.__typename = 'Duration';
            }
            const newData = Object.assign({ delivery: ev.target.value }, deliveryValues);
            if (newData.delivery != "CUSTOM") {
                client.writeFragment({
                    id: `SubscriptionBenefit:${id}`,
                    fragment: gql `
            fragment UpdateBenefitDeliveryComplex on SubscriptionBenefit {
              delivery
              deliveryFrequency
              shippingFrequency {
                __typename
                number
                period
              }
              firstShipmentDelay
            }
          `,
                    data: newData,
                });
            }
            // If custom, then only set the delivery field, leave the others as is
            else {
                client.writeFragment({
                    id: `SubscriptionBenefit:${id}`,
                    fragment: gql `
            fragment UpdateBenefitDeliverySimple on SubscriptionBenefit {
              delivery
            }
          `,
                    data: newData,
                });
            }
        };
    };
    const updateBenefitNameContent = (id) => {
        return (ev) => {
            pageHasUnsavedChangesVar(true);
            benefitRef.current = stripHtml(ev.target.value) || "Curated item from our collection";
            client.writeFragment({
                id: `SubscriptionBenefit:${id}`,
                fragment: gql `
          fragment UpdateBenefitName on Benefit {
            name
          }
        `,
                data: {
                    name: benefitRef.current
                },
                broadcast: false,
            });
        };
    };
    const removeBenefit = () => {
        destroyBenefit({
            variables: { id },
            // KGAUDIT - EditSubscriptionBenefitButton is not used anymore
            refetchQueries: ["EditSubscriptionBenefitButton"],
            update: (cache) => {
                cache.modify({
                    id: `MembershipTier:${tier.id}`,
                    fields: {
                        subscriptionBenefit: () => {
                            return null;
                        },
                        benefits: (existingBenefitRefs = [], { readField }) => {
                            if (!existingBenefitRefs) {
                                existingBenefitRefs = [];
                            }
                            return existingBenefitRefs.filter(ref => readField('id', ref) !== id);
                        }
                    }
                });
            }
        });
    };
    const reallyRemoveBenefit = () => {
        setShowModal(true);
    };
    const confirmRemoveBenefit = () => {
        removeBenefit();
        setShowModal(false);
    };
    const confirmRemoveBenefitModal = (_jsx(ModalConfirmation, { message: "Are you sure? ", buttonConfirm: confirmRemoveBenefit, buttonConfirmContents: `Yes, delete this box`, buttonReject: () => setShowModal(false), buttonRejectContents: `Cancel`, children: _jsxs("div", { className: "my-4 flex", children: ["This will delete all scheduled shipments for ", ((_a = benefit.fulfillments) === null || _a === void 0 ? void 0 : _a.length) > 2 ? "all " : "", (_b = benefit.fulfillments) === null || _b === void 0 ? void 0 : _b.length, " subscriber", ((_c = benefit.fulfillments) === null || _c === void 0 ? void 0 : _c.length) > 1 ? "s " : "", " of this tier. You will not be able to undo this action."] }) }));
    const handleClickEdit = () => {
        navigate(`/memberships/${benefit.membershipTiers[0].id}/subscription/${id}`);
        // This selects the name of the benefit, but for now let's try just opening the main screen.
        //
        // setTimeout(() => {
        //   updateBenefitNameRef.current.focus()
        //   setTimeout(() => {
        //     document.execCommand('selectAll', false, null)
        //   }, 0)
        // })
    };
    benefitRef.current = benefitRef.current || data.benefit.name;
    const pluralPeriods = benefit.deliveryFrequency > 1;
    const periodText = periodMap[benefit.shippingFrequency.period];
    const deliveryText = () => {
        if (benefit.delivery === DeliveryStrategy.Custom) {
            return `every ${benefit.deliveryFrequency} ${(pluralPeriods ? periodText.more : periodText.one).toLowerCase()}`;
        }
        else {
            return deliveryMapper[benefit.delivery];
        }
    };
    const needsAction = ((_d = benefit === null || benefit === void 0 ? void 0 : benefit.products) === null || _d === void 0 ? void 0 : _d.some(product => { var _a; return (_a = product.variants) === null || _a === void 0 ? void 0 : _a.some(variant => variant.curationStatus != "APPROVED"); })) && currentShopifyVersion == 2;
    return (_jsxs("li", { className: "relative w-full mb-4 leading-6 text-center", children: [showModal ? confirmRemoveBenefitModal : null, _jsxs("div", { className: "relative", children: [_jsx(BsBox, { className: "absolute inline-block", style: { width: "1.3em", top: "0.25em", left: "0.3em", right: "0", verticalAlign: "top", marginRight: "0.3em", marginLeft: "-0.3em" }, onClick: handleClickEdit }), _jsx(ContentEditable, { contentEditable: true, html: benefitRef.current, id: `benefit-name-${benefit.id}`, className: "py-1 px-2 inline-block text-center mr-2 underline focus:ring-transparent focus:outline-none focus:decoration-salmon-600 focus-within:decoration-salmon-600 selection:underline selection:bg-selectionBlue selection:decoration-salmon-600", style: { width: '78%' }, innerRef: updateBenefitNameRef, onChange: updateBenefitNameContent(benefit.id), onKeyPress: preventEnter }), _jsx(SvgX, { onClick: ((_e = benefit.fulfillments) === null || _e === void 0 ? void 0 : _e.length) > 0 ? reallyRemoveBenefit : removeBenefit, className: "text-wfGray-600 cursor-pointer fill-current absolute", style: { top: "0.225em", right: "-0.3em", transform: "scale(.7)" } })] }), _jsx("div", { className: "border border-wfGray-600 pr-2 rounded-none hover:border-wfGray-800 my-2", children: _jsxs("select", { onChange: updateBenefitDelivery(benefit.id), value: benefit.delivery, className: "font-heavy font-bold block mx-auto text-center pl-3 py-2 focus:outline-none cursor-pointer", style: { textAlignLast: "center" }, children: [_jsx("option", { className: "text-center", value: "ANNUAL", children: "delivered yearly" }), _jsx("option", { className: "text-center", value: "QUARTERLY", children: "delivered every 3 months" }), _jsx("option", { className: "text-center", value: "MONTHLY", children: "delivered monthly" }), _jsx("option", { className: "text-center", value: "BIWEEKLY", children: "delivered every 2 weeks" }), _jsx("option", { className: "text-center", value: "WEEKLY", children: "delivered weekly" }), _jsx("option", { className: "text-center", value: "CUSTOM", children: (((_f = benefit.shippingFrequency) === null || _f === void 0 ? void 0 : _f.number) == 1 && ((_g = benefit.shippingFrequency) === null || _g === void 0 ? void 0 : _g.period) == "MONTH" && benefit.deliveryFrequency == 3)
                                ? "delivered every 3 months (custom)"
                                : (((_h = benefit.shippingFrequency) === null || _h === void 0 ? void 0 : _h.number) == 1 && ((_j = benefit.shippingFrequency) === null || _j === void 0 ? void 0 : _j.period) == "MONTH" && benefit.deliveryFrequency == 12)
                                    ? "delivered every year (custom)"
                                    : "delivered on a custom schedule" })] }) }), _jsxs(Button, { className: "my-2", variant: (needsAction ? ButtonVariant.SalmonOutlined : ButtonVariant.Outlined), onClick: handleClickEdit, children: [_jsx(SvgSettingsGear24Px, { className: "text-wfGray-600 inline cursor-pointer align-middle fill-current mr-2" }), _jsx("span", { className: "align-middle", children: "Subscription Box Settings" })] }), needsAction && (_jsx(IconContext.Provider, { value: {
                    className: "inline mr-2 text-wfRed-600",
                    size: "24px",
                }, children: _jsxs("div", { className: "flex items-center justify-center text-sm text-wfRed-600 my-2", children: [_jsx(RiErrorWarningLine, {}), "Has curations pending your approval."] }) })), _jsx("div", { className: "w-full my-2" })] }, `benefit-id-${benefit.id}`));
};
const SortableBenefitWrapper = ({ benefit, tier }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging, } = useSortable({ id: benefit.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };
    return (_jsx("div", { ref: setNodeRef, style: style, children: _jsx(RegularBenefitComponent, { tier: tier, benefit: benefit, dragHandleProps: Object.assign(Object.assign({}, attributes), listeners), isDragging: isDragging }) }));
};
const BenefitList = ({ tier, position, kind }) => {
    const client = useApolloClient();
    const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
    }));
    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id)
            return;
        const activeId = active.id.toString();
        const overId = over.id.toString();
        // Read all benefits using the query
        const { membershipTier } = client.readQuery({
            query: GetAllRegularBenefitsDocument,
            variables: { tierId: tier.id }
        });
        // Collect all unique regular benefits across all tiers
        const uniqueBenefits = new Map();
        membershipTier.business.membershipTiers.forEach((tier) => {
            tier.benefits.forEach((benefit) => {
                if (benefit.__typename === 'RegularBenefit') {
                    if (!uniqueBenefits.has(benefit.id)) {
                        uniqueBenefits.set(benefit.id, {
                            id: benefit.id,
                            order: benefit.order,
                            ref: client.cache.identify({
                                __typename: 'RegularBenefit',
                                id: benefit.id
                            })
                        });
                    }
                }
            });
        });
        // Get sorted array of all regular benefits
        const allRegularBenefits = Array.from(uniqueBenefits.values())
            .sort((a, b) => a.order - b.order);
        // Find the positions of the benefits we're reordering
        const oldIndex = allRegularBenefits.findIndex(benefit => benefit.id === activeId);
        const overIndex = allRegularBenefits.findIndex(benefit => benefit.id === overId);
        if (oldIndex === -1 || overIndex === -1)
            return;
        // Perform the reorder
        const [movedBenefit] = allRegularBenefits.splice(oldIndex, 1);
        allRegularBenefits.splice(overIndex, 0, movedBenefit);
        // Update all benefits with new sequential orders
        allRegularBenefits.forEach((benefit, index) => {
            client.writeFragment({
                id: benefit.ref,
                fragment: gql `
          fragment UpdateBenefitOrder on RegularBenefit {
            order
          }
        `,
                data: {
                    order: index
                }
            });
        });
    };
    return (_jsx("ul", { children: kind === "subscription" ? (tier.benefits
            .filter(isSubscriptionBenefit)
            .map(benefit => (_jsx(SubscriptionBenefitComponent, { tier: tier, benefit: benefit, position: position }, benefit.id)))) : kind === "welcome" ? (tier.welcomeGift && (_jsx(WelcomeGiftComponent, { tier: tier, benefit: tier.welcomeGift }, tier.welcomeGift.id))) : (_jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragEnd: handleDragEnd, children: _jsx("div", { className: "min-h-[50px]", children: (() => {
                    const sortedBenefits = tier.benefits
                        .filter(isRegularBenefit)
                        .sort((a, b) => a.order - b.order);
                    return (_jsx(SortableContext, { items: sortedBenefits.map(b => b.id), strategy: verticalListSortingStrategy, children: sortedBenefits.map(benefit => (_jsx(SortableBenefitWrapper, { benefit: benefit, tier: tier }, benefit.id))) }));
                })() }) })) }));
};
export default BenefitList;
